/**
 * TAG/NACHT FUER DIE BUEHNENSEITEN (Rafael, 03.10.2026) — die CUI-Seite des Zurufs.
 *
 * Die Seite einer Buehne (`/api/guide/buehnen/<id>/seite.html`) laeuft abgeschottet (sandbox,
 * fremder Ursprung) in einer Browser-Kachel: in Electron als <webview>, im Browser als
 * <iframe>. Sie sieht den localStorage der CUI nicht. Die Auslieferung setzt ihr deshalb ein
 * kleines Skript ein (server/lib/buehnen-seite-farbschema.ts), das auf
 * `postMessage({ cuiFarbschema: 'hell' | 'dunkel' })` hoert.
 *
 * Hier wird gerufen — ohne die Kachel-Komponente anzufassen:
 *  - beim Laden jeder Kachel: Ereignisse `dom-ready` (webview) und `load` (iframe) feuern nicht
 *    nach oben, kommen aber in der Capture-Phase am Dokument vorbei;
 *  - bei jedem Wechsel des Umschalters: an alle Kacheln, die gerade eine Buehnenseite zeigen.
 * Dunkel ist die Vorgabe der Seite; gerufen wird trotzdem in beiden Richtungen, damit ein
 * Wechsel zurueck auch ohne Neuladen wirkt.
 */
type Schema = 'dunkel' | 'hell';

const SEITE = /\/api\/guide\/buehnen\/[^/?#]+\/seite(?:\.html)?\/?(?:[?#]|$)/i;

interface WebviewArtig extends HTMLElement {
  getURL?: () => string;
  executeJavaScript?: (code: string) => Promise<unknown>;
}

function adresse(el: Element): string {
  const wv = el as WebviewArtig;
  if (el.tagName === 'WEBVIEW') {
    try { const u = wv.getURL?.(); if (u) return u; } catch { /* noch nicht bereit: Attribut lesen */ }
  }
  return el.getAttribute('src') ?? '';
}

export function istBuehnenSeite(url: string): boolean {
  try { return SEITE.test(new URL(url, window.location.href).pathname + '?'); } catch { return false; }
}

/** Ruft EINE Kachel. Gibt zurueck, ob sie eine Buehnenseite zeigte. */
export function rufeKachel(el: Element, schema: Schema): boolean {
  if (!istBuehnenSeite(adresse(el))) return false;
  const nachricht = { cuiFarbschema: schema };
  if (el.tagName === 'WEBVIEW') {
    const wv = el as WebviewArtig;
    if (!wv.executeJavaScript) return false;
    // Vor dom-ready wirft executeJavaScript — dann kommt der Zuruf mit dom-ready nach.
    try {
      wv.executeJavaScript(`window.postMessage(${JSON.stringify(nachricht)}, '*')`)
        .catch(err => console.debug('[buehnenSeitenFarbschema] Kachel noch nicht bereit:', err));
    } catch (err) { console.debug('[buehnenSeitenFarbschema] Kachel noch nicht bereit:', err); }
    return true;
  }
  if (el.tagName === 'IFRAME') {
    (el as HTMLIFrameElement).contentWindow?.postMessage(nachricht, '*');
    return true;
  }
  return false;
}

export function reicheAnBuehnenSeiten(schema: Schema): number {
  let n = 0;
  document.querySelectorAll('webview, iframe').forEach(el => { if (rufeKachel(el, schema)) n++; });
  return n;
}

let installiert = false;
export function beobachteBuehnenSeiten(aktuell: () => Schema) {
  if (installiert || typeof document === 'undefined') return;
  installiert = true;
  const beimLaden = (e: Event) => {
    const el = e.target;
    if (el instanceof Element && (el.tagName === 'WEBVIEW' || el.tagName === 'IFRAME')) rufeKachel(el, aktuell());
  };
  document.addEventListener('dom-ready', beimLaden, true);
  document.addEventListener('load', beimLaden, true);
}
